import { gql, useLazyQuery } from '@apollo/client'
import { SearchAutocomplete, SearchImperativeRef, useDebounceCallback } from '@farol-ds/react'
import { ChangeEvent, useRef, useState } from 'react'

import { Topic } from '../types'
import { TopicSearchQuery, TopicSearchQueryVariables } from './__generated__/search-field.graphql'

export interface SearchFieldProps {
  addTopic: (topic: Topic) => void
}

type TopicItem = NonNullable<TopicSearchQuery['root']['search']>['searchDocuments'][0]

const MIN_SEARCH_CHARS = 4
const NUM_RESULTS = 10

export function SearchField(props: SearchFieldProps) {
  const { addTopic } = props
  const [topics, setTopics] = useState<Topic[]>([])
  const imperativeRef = useRef<SearchImperativeRef>(null)

  const lastSelectedOptionRef = useRef<string | null>(null)

  const handleValueChange = useDebounceCallback(
    (evt: ChangeEvent<HTMLInputElement>, value: string) => {
      if (value === lastSelectedOptionRef.current || value.length < MIN_SEARCH_CHARS) {
        return
      }
      fetchProfile({ variables: { q: value, limit: NUM_RESULTS } }).then((res) => {
        setTopics(
          (res.data?.root.search?.searchDocuments || [])
            .filter((item): item is TopicItem => !!item.topic?.tid)
            .map((item) => ({
              title: item.title || '',
              url: item.url || '#',
              tid: item.topic?.tid || 0,
            }))
        )
      })
    },
    250
  )

  const getOptions = () =>
    topics.map((item) => ({
      value: item.title,
      id: item.tid,
      label: item.title,
    }))

  const [fetchProfile, { loading }] = useLazyQuery<TopicSearchQuery, TopicSearchQueryVariables>(
    FETCH_TOPICS,
    {
      variables: { q: '', limit: NUM_RESULTS },
    }
  )

  return (
    <SearchAutocomplete type="secondary">
      <SearchAutocomplete.Trigger
        name="q"
        loading={loading}
        onValueChange={handleValueChange}
        placeholder="Pesquise e adicione tópicos"
        searchProps={{ imperativeRef }}
      />
      <SearchAutocomplete.Menu
        dangerouslyLabelAsHTML
        options={getOptions()}
        onOptionSelect={(option) => {
          const topic = topics.find((t) => t.tid === option.id)
          if (topic) {
            addTopic(topic)
            setTopics([])
            lastSelectedOptionRef.current = option.value
            setTimeout(() => imperativeRef.current?.clear(), 0)
          }
        }}
      />
    </SearchAutocomplete>
  )
}

export const FETCH_TOPICS = gql`
  query TopicSearch($q: String!, $limit: Int!) {
    root {
      search(
        query: $q
        first: $limit
        artifact: "TOPICOS"
        uniqueField: "id-topico"
        useAutocomplete: true
        maxLoadUnique: 100
        legalSearch: false
        order: "_totalArtigo"
        filterTopicType: [
          "ASSUNTO_JURIDICO"
          "FONTE"
          "LOCALIDADE_CIDADE"
          "LOCALIDADE_ESTADO"
          "LOCALIDADE_PAIS"
          "PROFISSAO"
          "COMUM"
        ]
      ) {
        searchDocuments {
          topic {
            tid
          }
          title
          url
        }
      }
    }
  }
`
